import { useCallback, useMemo } from 'react';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Form, FormField } from 'core/components';
import { InputType } from 'core/enums';
import { FormProvider, useAgGridClientTableContext, useNotificationContext } from 'core/states';
import { CorporateUserType } from 'corporate/enums';
import { corporateUserService } from 'corporate/services';
import { ICorporateUserAutocomplete } from 'corporate/types';
import { RemoveVendorsButton } from 'jobs/components/JobDetails/tabs/VendorList/actions';
import { useJobVendorActions } from 'jobs/hooks';
import { IJobVendor } from 'jobs/types';

import { schema } from './schema';

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  jobId: number;
  projectId: number;
  vendor?: IJobVendor;
}

interface IVendorFormData {
  corporateUser: ICorporateUserAutocomplete;
}

export const UpsertVendor = ({ isOpen, onClose, jobId, projectId, vendor }: IProps) => {
  const { t } = useTranslation();

  const { clearNotification } = useNotificationContext();
  const { updateRows } = useAgGridClientTableContext();

  const { upsertJobVendor } = useJobVendorActions();

  const isEdit = Boolean(vendor?.id);

  const defaultValues = useMemo(() => {
    if (!isOpen || !isEdit) {
      return {};
    }

    return {
      corporateUser: {
        id: vendor.foreignCorporateUserId,
        companyId: vendor.foreignCompanyId,
      },
    };
  }, [isOpen, isEdit, vendor]);

  const removeButton = useMemo(() => {
    return isEdit ? <RemoveVendorsButton jobId={jobId} vendors={[vendor]} /> : null;
  }, [vendor, jobId, isEdit]);

  const handleOnClose = useCallback(
    (reset: UseFormReset<IVendorFormData>) => {
      reset(defaultValues);
      clearNotification();
      onClose();
    },
    [defaultValues, onClose, clearNotification]
  );

  const onSubmitSuccess = useCallback(
    (reset: UseFormReset<IVendorFormData>, updatedVendor: IJobVendor) => {
      const updateRowsData = isEdit ? { update: [updatedVendor] } : { add: [updatedVendor] };

      updateRows(updateRowsData);

      setTimeout(() => {
        handleOnClose(reset);
      }, 1000);
    },
    [isEdit, updateRows, handleOnClose]
  );

  const onSubmit = useCallback(
    (reset: UseFormReset<IVendorFormData>) => async (data: IVendorFormData) => {
      if (!data.corporateUser) {
        return;
      }

      const { success, bomVendorCompanyAssociation: updatedVendor } = await upsertJobVendor({
        bomVendorCompanyAssociation: {
          bomId: jobId,
          foreignCorporateUserId: data.corporateUser.id,
          foreignCompanyId: data.corporateUser.companyId,
          ...(isEdit && { id: vendor?.id }),
        },
      });

      if (!success) {
        return;
      }

      onSubmitSuccess(reset, updatedVendor);
    },
    [jobId, isEdit, vendor, upsertJobVendor, onSubmitSuccess]
  );

  return (
    <FormProvider schema={schema()} defaultValues={defaultValues} resetDefaultValues>
      <Form
        isOpen={isOpen}
        onClose={handleOnClose}
        title={isEdit ? t('titleEditVendor') : t('titleAddVendor')}
        subtitle={vendor?.foreignContactGroupName}
        onSubmit={onSubmit}
        submitBtnLabel={isEdit ? t('buttonLabelUpdateVendor') : t('buttonLabelAddVendor')}
        extraButtons={removeButton}
      >
        {!isEdit && (
          <FormField
            name="corporateUser"
            type={InputType.AUTOCOMPLETE}
            inputProps={{
              label: t('labelQuoteContact'),
              placeholder: t('placeholderSelectVendor'),
              getOptions: corporateUserService.getCorporateForeignUsersAutocomplete,
              requestPayload: {
                filters: {
                  userType: CorporateUserType.VENDOR,
                  projectId,
                },
              },
              getKey: (option: ICorporateUserAutocomplete) =>
                `${option.id}-${option.contactGroup?.id}`,
              getLabel: (option: ICorporateUserAutocomplete) =>
                option.contactGroup?.name
                  ? `${option.name} (${option.contactGroup.name})`
                  : option.name,
              disableClearable: true,
              required: true,
            }}
          />
        )}
      </Form>
    </FormProvider>
  );
};
